<template>
  <div>
    <!-- Botão de abertura ou erro de cadastro -->
    <div v-if="errors.length == 0" class="mt-5 text-center">
      <v-btn
        class="btn-modern btn-modern-add"
        @click="showMemed"
        :disabled="memedLoading || !resident"
        :loading="memedLoading"
        large
        aria-label="Abrir receita digital"
      >
        <i class="fas fa-prescription-bottle-alt mr-2"></i>
        <span v-if="!memedLoading">Abrir Receita Digital</span>
        <span v-else>Carregando...</span>
      </v-btn>
    </div>

    <div v-if="errors.length > 0" class="memed-error-box">
      <div class="memed-error-icon">
        <i class="fas fa-exclamation-triangle"></i>
      </div>
      <div class="memed-error-content">
        <h3 class="memed-error-title">Cadastro de Prescritor necessário</h3>
        <p class="memed-error-text">
          Para utilizar a prescrição digital, é preciso se cadastrar como
          prescritor na Memed.
        </p>
        <p class="memed-error-text">
          Acesse o menu <b>Usuários</b>, vá até a <b>Aba Memed</b> e realize seu
          cadastro.
        </p>
      </div>
    </div>

    <!-- ─────────────────────────────────────────────
         TABELA DE HISTÓRICO DE PRESCRIÇÕES
         Exibida apenas quando há residente definido
    ───────────────────────────────────────────── -->
    <div v-if="resident" class="prescricoes-historico border p-2">
      <!-- Cabeçalho da seção -->
      <div class="prescricoes-header">
        <div class="prescricoes-header-left">
          <i class="fas fa-history prescricoes-header-icon"></i>
          <span class="prescricoes-header-title">Histórico de Prescrições</span>
        </div>
        <button
          class="prescricoes-refresh-btn"
          @click="loadPrescricoes"
          :disabled="loadingPrescricoes"
          aria-label="Atualizar lista de prescrições"
        >
          <i
            class="fas fa-sync-alt"
            :class="{ 'fa-spin': loadingPrescricoes }"
          ></i>
        </button>
      </div>

      <!-- Estado: carregando -->
      <div
        v-show="loadingPrescricoes"
        class="prescricoes-loading"
        role="status"
        aria-live="polite"
      >
        <i class="fas fa-circle-notch fa-spin"></i>
        <span>Carregando prescrições...</span>
      </div>

      <!-- Estado: sem prescrições -->
      <div
        v-show="prescricoes.length == 0"
        class="prescricoes-vazio"
        role="status"
      >
        <i class="fas fa-file-medical prescricoes-vazio-icon"></i>
        <p class="prescricoes-vazio-text">
          Nenhuma prescrição registrada para este residente.
        </p>
      </div>

      <!-- Tabela de prescrições -->
      <div v-show="prescricoes.length > 0" class="prescricoes-table-wrap">
        <table
          class="prescricoes-table"
          aria-label="Histórico de prescrições do residente"
        >
          <thead>
            <tr>
              <th scope="col">Data</th>
              <th scope="col">Médico</th>
              <th scope="col">Medicamentos</th>
              <th scope="col">Tipo</th>
              <th scope="col" class="col-acoes">Ações</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(prescricao, index) in prescricoes">
              <!-- Linha principal da prescrição -->
              <tr
                :key="'p-' + index"
                :class="{ 'tr-expandida': prescricao._expandida }"
              >
                <td data-label="Data">
                  <span class="prescricao-data">{{ prescricao.data }}</span>
                  <span class="prescricao-hora">{{ prescricao.horario }}</span>
                </td>

                <td data-label="Médico">{{ prescricao.nome_medico }}</td>

                <td data-label="Medicamentos">
                  <!-- Exibe até 2 medicamentos inline; o resto no expand -->
                  <span
                    v-for="(med, mi) in prescricao.medicamentos.slice(0, 2)"
                    :key="mi"
                    class="med-badge"
                    >{{ med.nome }}</span
                  >
                  <span
                    v-if="prescricao.medicamentos.length > 2"
                    class="med-badge med-badge-mais"
                    >+{{ prescricao.medicamentos.length - 2 }} mais</span
                  >
                </td>

                <td data-label="Tipo">
                  <span
                    class="receituario-badge"
                    :class="receituarioCss(prescricao.medicamentos)"
                  >
                    {{ receituarioLabel(prescricao.medicamentos) }}
                  </span>
                </td>

                <td data-label="Ações" class="col-acoes">
                  <!-- Ver detalhes (expand) -->
                  <button
                    class="btn-acao btn-detalhe"
                    @click="toggleExpand(prescricao)"
                    :aria-expanded="prescricao._expandida ? 'true' : 'false'"
                    :aria-label="
                      prescricao._expandida
                        ? 'Ocultar detalhes'
                        : 'Ver detalhes'
                    "
                    :title="
                      prescricao._expandida
                        ? 'Ocultar detalhes'
                        : 'Ver detalhes'
                    "
                  >
                    <i
                      class="fas"
                      :class="
                        prescricao._expandida
                          ? 'fa-chevron-up'
                          : 'fa-chevron-down'
                      "
                    ></i>
                  </button>

                  <!-- Abrir PDF — tag <a> completa e corrigida -->
                  <a
                    v-if="prescricao.link"
                    :href="prescricao.link"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="btn-acao btn-pdf"
                    title="Abrir receita em PDF"
                    aria-label="Abrir receita em PDF"
                  >
                    <i class="fas fa-file-pdf"></i>
                  </a>
                </td>
              </tr>

              <!-- Linha de detalhes expandida -->
              <tr
                v-if="prescricao._expandida"
                :key="'d-' + index"
                class="tr-detalhe"
              >
                <td colspan="5">
                  <div class="detalhe-wrap">
                    <table
                      class="detalhe-table"
                      aria-label="Detalhes dos medicamentos"
                    >
                      <thead>
                        <tr>
                          <th scope="col">Medicamento</th>
                          <th scope="col">Posologia</th>
                          <th scope="col">Receituário</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(med, mi) in prescricao.medicamentos"
                          :key="mi"
                        >
                          <td>{{ med.nome }}</td>
                          <td v-html="sanitizePosologia(med.posologia)"></td>
                          <td>{{ med.receituario }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
/* global MdHub, MdSinapsePrescricao */

import toast from "../../../lib/ToastNotification";
import api from "../../../services/api";
import moment from "moment";

export default {
  name: "DigitalRecipe",

  data() {
    return {
      errors: [],
      tokenUser: null,
      loading: false,
      memedLoaded: false,
      memedVisible: false,
      memedLoading: false,
      memedReady: false,
      memedError: false,
      memedTimeout: null,
      memedShouldOpen: false,

      // ── Histórico de prescrições ──
      prescricoes: [],
      loadingPrescricoes: false,
    };
  },

  props: {
    resident: { type: [Object, undefined] },
  },

  watch: {
    // Recarrega o histórico sempre que o residente mudar
    resident(newVal) {
      if (newVal) this.loadPrescricoes();
    },
  },

  async mounted() {
    await this.getTokenByUserLoggedIn();
    this.checkMemedReadyOrLoad();

    if (this.resident) this.loadPrescricoes();
  },

  beforeDestroy() {
    window.__memedModuleInitListener = false;
  },

  methods: {
    // ─────────────────────────────────────────
    // HISTÓRICO: busca prescrições do residente
    // ─────────────────────────────────────────
    async loadPrescricoes() {
      if (!this.resident) return;

      try {
        this.loadingPrescricoes = true;

        const response = await api.get(
          `/rest-home/memed/prescription/${this.resident.id}`
        );

        // A API retorna um array direto, não um objeto { prescricoes: [] }
        const lista = Array.isArray(response.data.prescricoes)
          ? response.data.prescricoes
          : [];
        this.prescricoes = lista.map((p) => ({
          ...p,
          _expandida: false,
        }));
      } catch (error) {
        console.error("[Memed] Erro ao carregar prescrições:", error);
        toast("Erro ao carregar histórico de prescrições.", "error");
      } finally {
        this.loadingPrescricoes = false;
      }
    },

    // ─────────────────────────────────────────
    // HISTÓRICO: salva prescrição no back-end
    // após o evento prescricaoImpressa da Memed
    // ─────────────────────────────────────────
    async salvarPrescricao(prescriptionData) {
      if (!this.resident) return;

      try {
        await api.post("/rest-home/memed/prescription", {
          resident_id: this.resident.id,
          prescricao: prescriptionData,
        });

        await this.loadPrescricoes();
      } catch (error) {
        console.error("[Memed] Erro ao salvar prescrição:", error);
      }
    },

    // ─────────────────────────────────────────
    // HISTÓRICO: expande/colapsa linha de detalhe
    // ─────────────────────────────────────────
    toggleExpand(prescricao) {
      prescricao._expandida = !prescricao._expandida;
    },

    // ─────────────────────────────────────────
    // HELPERS de exibição
    // ─────────────────────────────────────────

    /**
     * Remove tags HTML da posologia para exibir de forma segura.
     * Mantém apenas <p> e <br> para preservar quebras de linha.
     */
    sanitizePosologia(posologia) {
      if (!posologia) return "—";
      return posologia.replace(/<(?!\/?(p|br)(?=>|\s.*>))\/?.*?>/gi, "").trim();
    },

    /**
     * Rótulo do tipo de receituário mais restritivo
     * encontrado nos medicamentos da prescrição.
     * Cobre todos os tipos retornados pela Memed.
     */
    receituarioLabel(medicamentos) {
      if (!medicamentos || medicamentos.length === 0) return "—";
      const tipos = medicamentos.map((m) =>
        (m.receituario || "").toLowerCase()
      );

      if (tipos.some((t) => t.includes("notif"))) return "Notificação";
      if (tipos.some((t) => t.includes("especial"))) return "Especial";
      if (tipos.some((t) => t.includes("antibi"))) return "Antibiótico";
      return "Simples";
    },

    /**
     * Classe CSS correspondente ao tipo de receituário.
     */
    receituarioCss(medicamentos) {
      const label = this.receituarioLabel(medicamentos);
      if (label === "Notificação") return "badge-notificacao";
      if (label === "Especial") return "badge-especial";
      if (label === "Antibiótico") return "badge-antibiotico";
      return "badge-simples";
    },

    // ─────────────────────────────────────────
    // MEMED: token do prescritor logado
    // ─────────────────────────────────────────
    async getTokenByUserLoggedIn() {
      try {
        this.loading = true;
        const response = await api.get(
          "/rest-home/memed/prescritor/user-token"
        );
        const { error, errors, tokenUser } = response.data;
        if (!error) {
          this.errors = [];
          this.tokenUser = tokenUser;
        } else {
          this.errors = errors;
        }
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.error(error);
      }
    },

    // ─────────────────────────────────────────
    // MEMED: abre o módulo de prescrição
    // ─────────────────────────────────────────
    async showMemed() {
      if (!this.resident) {
        toast("Aguarde, carregando MEMED!", "error");
        return;
      }

      this.memedError = false;
      this.memedLoading = true;
      this.memedShouldOpen = true;

      if (
        typeof MdHub != "undefined" &&
        MdHub.module &&
        typeof MdHub.module.hide == "function"
      ) {
        MdHub.module.hide("plataforma.prescricao");
      }

      if (this.memedReady) {
        await this.openMemed();
      }
    },

    /**
     * Sanitiza o telefone para o formato aceito pela Memed:
     * somente números, com DDD, sem DDI.
     */
    sanitizePhone(phone) {
      if (!phone) return "";
      let digits = phone.replace(/\D/g, "");
      if (digits.startsWith("55") && digits.length > 11) {
        digits = digits.slice(2);
      }
      if (digits.length < 10 || digits.length > 11) {
        console.warn("[Memed] Telefone inválido:", phone, "→", digits);
        return "";
      }
      return digits;
    },

    async openMemed() {
      try {
        let { resident } = this;
        if (resident) {
          let { id, name, date_of_birth, cpf, phone } = resident;
          const telefoneSanitizado = this.sanitizePhone(phone);
          await MdHub.command.send("plataforma.prescricao", "setPaciente", {
            idExterno: id,
            nome: name,
            telefone: telefoneSanitizado,
            data_nascimento: date_of_birth
              ? moment(date_of_birth).format("DD/MM/YYYY")
              : "",
            cpf: cpf ? cpf : "",
          });
        }
      } catch (error) {
        toast("Erro ao enviar paciente para o Memed!", "error");
        this.memedLoading = false;
        return;
      }

      if (
        typeof MdHub != "undefined" &&
        MdHub.module &&
        typeof MdHub.module.show == "function"
      ) {
        MdHub.module.show("plataforma.prescricao");
        this.memedShouldOpen = false;
        this.memedLoading = false;

        // Reenvia o paciente após o módulo renderizar
        // para garantir que os dados do prontuário sobrescrevam o cache da Memed
        setTimeout(async () => {
          try {
            let { resident } = this;
            if (!resident) return;
            let { id, name, date_of_birth, cpf, phone } = resident;
            const telefoneSanitizado = this.sanitizePhone(phone);
            await MdHub.command.send("plataforma.prescricao", "setPaciente", {
              idExterno: id,
              nome: name,
              telefone: telefoneSanitizado,
              data_nascimento: date_of_birth
                ? moment(date_of_birth).format("DD/MM/YYYY")
                : "",
              cpf: cpf ? cpf : "",
            });
          } catch (e) {
            console.warn("[Memed] Falha ao reenviar paciente pós-show:", e);
          }
        }, 800);
      }
    },

    // ─────────────────────────────────────────
    // MEMED: inicialização do script
    // ─────────────────────────────────────────
    checkMemedReadyOrLoad() {
      if (document.querySelector('script[data-memed="true"]')) {
        if (
          typeof MdSinapsePrescricao != "undefined" &&
          MdSinapsePrescricao.event &&
          typeof MdHub != "undefined" &&
          MdHub.module &&
          typeof MdHub.module.show == "function"
        ) {
          this.memedReady = true;
          this.memedLoading = false;
          this.memedError = false;
        } else {
          this.listenModuleInit();
        }
      } else {
        this.loadMemedScript();
      }
    },

    loadMemedScript() {
      if (document.querySelector('script[data-memed="true"]')) {
        this.listenModuleInit();
        return;
      }

      if (!this.tokenUser) {
        return;
      }

      this.memedLoading = true;
      this.memedLoaded = false;
      this.memedError = false;

      const script = document.createElement("script");
      script.src = "https://partners.memed.com.br/integration.js";
      script.dataset.token = this.tokenUser;
      script.setAttribute("data-memed", "true");
      script.onload = this.onMemedScriptLoaded;
      script.onerror = () => {
        this.memedLoading = false;
        this.memedError = true;
      };

      document.body.appendChild(script);

      if (this.memedTimeout) clearTimeout(this.memedTimeout);
      this.memedTimeout = setTimeout(() => {
        if (!this.memedLoaded) {
          this.memedLoading = false;
          this.memedError = true;
        }
      }, 20000);
    },

    onMemedScriptLoaded() {
      this.memedLoaded = true;
      this.memedLoading = false;
      this.listenModuleInit();
    },

    listenModuleInit() {
      if (window.__memedModuleInitListener) return;

      window.__memedModuleInitListener = true;

      const checkAndAddListener = () => {
        if (
          typeof MdSinapsePrescricao != "undefined" &&
          MdSinapsePrescricao.event
        ) {
          MdSinapsePrescricao.event.add("core:moduleInit", async (module) => {
            if (module.name == "plataforma.prescricao") {
              this.memedReady = true;
              this.memedLoading = false;
              this.memedError = false;

              // Escuta prescrição emitida e salva automaticamente no back-end
              MdHub.event.add(
                "prescricaoImpressa",
                async (prescriptionData) => {
                  await this.salvarPrescricao(prescriptionData);
                }
              );

              if (this.memedShouldOpen) {
                await this.openMemed();
              }
            }
          });
        } else {
          setTimeout(checkAndAddListener, 500);
        }
      };

      checkAndAddListener();
    },
  },
};
</script>

<style scoped>
/* ── Erro de cadastro ── */
.memed-error-box {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
  border-radius: 16px;
  padding: 24px 32px;
  margin: 32px 0;
  box-shadow: 0 2px 12px rgba(220, 38, 38, 0.06);
}

.memed-error-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fee2e2;
  border-radius: 50%;
  width: 56px;
  height: 56px;
}

.memed-error-icon i {
  color: #dc2626;
  font-size: 32px;
}

.memed-error-content {
  flex: 1;
}

.memed-error-title {
  color: #dc2626;
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.memed-error-text {
  color: #991b1b;
  font-size: 15px;
  margin: 0 0 6px 0;
}

/* ── Seção de histórico ── */
.prescricoes-historico {
  margin-top: 32px;
}

.prescricoes-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.prescricoes-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.prescricoes-header-icon {
  color: #6366f1;
  font-size: 18px;
}

.prescricoes-header-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.prescricoes-refresh-btn {
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  color: #64748b;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
}

.prescricoes-refresh-btn:hover:not(:disabled) {
  background: #f1f5f9;
  color: #334155;
}

.prescricoes-refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Estado: carregando ── */
.prescricoes-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px;
  color: #64748b;
  font-size: 15px;
}

/* ── Estado: vazio ── */
.prescricoes-vazio {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  gap: 12px;
}

.prescricoes-vazio-icon {
  color: #cbd5e1;
  font-size: 40px;
}

.prescricoes-vazio-text {
  color: #94a3b8;
  font-size: 14px;
  margin: 0;
  text-align: center;
}

/* ── Tabela principal ── */
.prescricoes-table-wrap {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.prescricoes-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.prescricoes-table thead tr {
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
}

.prescricoes-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #475569;
  font-size: 13px;
  white-space: nowrap;
}

.prescricoes-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.1s;
}

.prescricoes-table tbody tr:hover {
  background: #f8fafc;
}

.tr-expandida {
  background: #f0f4ff !important;
}

.tr-detalhe td {
  padding: 0;
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
}

.detalhe-wrap {
  padding: 16px 20px;
}

.prescricoes-table td {
  padding: 12px 16px;
  color: #334155;
  vertical-align: middle;
}

/* Data + hora empilhados */
.prescricao-data {
  display: block;
  font-weight: 600;
  color: #1e293b;
}

.prescricao-hora {
  display: block;
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}

/* ── Badges de medicamento ── */
.med-badge {
  display: inline-block;
  background: #ede9fe;
  color: #5b21b6;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  margin-right: 4px;
  margin-bottom: 2px;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.med-badge-mais {
  background: #f1f5f9;
  color: #64748b;
}

/* ── Badges de receituário ── */
.receituario-badge {
  display: inline-block;
  border-radius: 20px;
  padding: 3px 12px;
  font-size: 12px;
  font-weight: 600;
}

.badge-simples {
  background: #dcfce7;
  color: #166534;
}

.badge-especial {
  background: #fef9c3;
  color: #854d0e;
}

.badge-antibiotico {
  background: #fff7ed;
  color: #9a3412;
}

.badge-notificacao {
  background: #fee2e2;
  color: #991b1b;
}

/* ── Botões de ação ── */
.col-acoes {
  width: 80px;
  text-align: center;
}

.btn-acao {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.15s, color 0.15s;
  text-decoration: none;
  margin: 0 2px;
}

.btn-detalhe {
  background: #f1f5f9;
  color: #475569;
}

.btn-detalhe:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.btn-pdf {
  background: #fee2e2;
  color: #dc2626;
}

.btn-pdf:hover {
  background: #fecaca;
  color: #991b1b;
}

/* ── Tabela de detalhe de medicamentos ── */
.detalhe-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.detalhe-table th {
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  font-size: 12px;
}

.detalhe-table td {
  padding: 8px 12px;
  color: #334155;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
}

.detalhe-table tr:last-child td {
  border-bottom: none;
}

/* ── Responsivo mobile ── */
@media (max-width: 768px) {
  .prescricoes-table thead {
    display: none;
  }

  .prescricoes-table tbody tr {
    display: block;
    margin-bottom: 12px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
  }

  .prescricoes-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 14px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 13px;
  }

  .prescricoes-table td::before {
    content: attr(data-label);
    font-weight: 600;
    color: #64748b;
    font-size: 12px;
    flex-shrink: 0;
    margin-right: 12px;
  }

  .tr-detalhe td {
    display: block;
  }

  .tr-detalhe td::before {
    display: none;
  }

  .col-acoes {
    width: auto;
  }
}
</style>